import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Row,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import { api } from "../../api/api";
import { IColumnSettings } from "../../types/columnSettings.type";

const { Title, Text } = Typography;

export default function ColumnSettingsPage() {
  const [columnSettings, setColumnSettings] = useState<IColumnSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingScreenKey, setSavingScreenKey] = useState<string | null>(null);
  const [resettingScreenKey, setResettingScreenKey] = useState<string | null>(null);

  const sortedSettings = useMemo(
    () =>
      [...columnSettings].sort((a, b) => a.label.localeCompare(b.label, "ru")),
    [columnSettings]
  );

  const loadColumnSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/settings/columns");
      setColumnSettings(response.data?.columnSettings || []);
    } catch (error: any) {
      setError(
        error.response?.data?.error || "Ошибка при загрузке настроек колонок"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadColumnSettings();
  }, []);

  const updateScreenColumns = (
    screenKey: string,
    updater: (current: IColumnSettings) => IColumnSettings
  ) => {
    setColumnSettings((prev) =>
      prev.map((item) => (item.screenKey === screenKey ? updater(item) : item))
    );
  };

  const handleColumnChange = (
    screenKey: string,
    columnKey: string,
    checked: boolean
  ) => {
    updateScreenColumns(screenKey, (current) => ({
      ...current,
      columns: {
        ...current.columns,
        [columnKey]: checked,
      },
    }));
  };

  const handleSelectAll = (screenKey: string) => {
    updateScreenColumns(screenKey, (current) => ({
      ...current,
      columns: current.availableColumns.reduce<Record<string, boolean>>(
        (acc, column) => {
          acc[column.key] = true;
          return acc;
        },
        {}
      ),
    }));
  };

  const handleDeselectAll = (screenKey: string) => {
    updateScreenColumns(screenKey, (current) => ({
      ...current,
      columns: current.availableColumns.reduce<Record<string, boolean>>(
        (acc, column) => {
          acc[column.key] = false;
          return acc;
        },
        {}
      ),
    }));
  };

  const handleSave = async (item: IColumnSettings) => {
    try {
      setSavingScreenKey(item.screenKey);
      const response = await api.post(`/settings/columns/${item.screenKey}`, {
        columns: item.columns,
      });

      const updatedSettings = response.data?.columnSettings as IColumnSettings | undefined;
      if (updatedSettings) {
        updateScreenColumns(item.screenKey, () => updatedSettings);
      }

      message.success(`Настройки "${item.label}" сохранены`);
    } catch (error: any) {
      message.error(
        error.response?.data?.error || "Ошибка при сохранении настроек колонок"
      );
    } finally {
      setSavingScreenKey(null);
    }
  };

  const handleReset = async (screenKey: string) => {
    try {
      setResettingScreenKey(screenKey);
      const response = await api.post(`/settings/columns/${screenKey}/reset`);
      const updatedSettings = response.data?.columnSettings as IColumnSettings | undefined;

      if (updatedSettings) {
        updateScreenColumns(screenKey, () => updatedSettings);
      }

      message.success("Настройки сброшены к значениям по умолчанию");
    } catch (error: any) {
      message.error(
        error.response?.data?.error || "Ошибка при сбросе настроек колонок"
      );
    } finally {
      setResettingScreenKey(null);
    }
  };

  if (loading) {
    return <Spin tip="Загрузка..." />;
  }

  if (error) {
    return <Alert message={error} type="error" showIcon />;
  }

  return (
    <div>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Title level={3} style={{ marginBottom: 8 }}>
            Колонки таблиц
          </Title>
          <Text type="secondary">
            Эти настройки принудительно применяются на страницах заказов,
            транзакций и логов партнёров для всех пользователей.
          </Text>
        </div>

        {sortedSettings.map((item) => (
          <Card
            key={item.screenKey}
            title={item.label}
            extra={
              <Space>
                <Button size="small" onClick={() => handleSelectAll(item.screenKey)}>
                  Выбрать все
                </Button>
                <Button size="small" onClick={() => handleDeselectAll(item.screenKey)}>
                  Снять все
                </Button>
                <Button
                  size="small"
                  onClick={() => handleReset(item.screenKey)}
                  loading={resettingScreenKey === item.screenKey}
                >
                  Сбросить
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleSave(item)}
                  loading={savingScreenKey === item.screenKey}
                >
                  Сохранить
                </Button>
              </Space>
            }
          >
            <Row gutter={[16, 12]}>
              {item.availableColumns.map((column) => (
                <Col key={column.key} xs={24} sm={12} md={8}>
                  <Checkbox
                    checked={!!item.columns[column.key]}
                    onChange={(event) =>
                      handleColumnChange(
                        item.screenKey,
                        column.key,
                        event.target.checked
                      )
                    }
                  >
                    {column.label}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Card>
        ))}
      </Space>
    </div>
  );
}
